export type ThemePreference = "dark" | "light";

export const THEME_STORAGE_KEY = "tktic_theme";
const LEGACY_THEME_STORAGE_KEY = "replay_theme";
const DEFAULT_THEME: ThemePreference = "dark";
const THEME_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

function isThemePreference(value: string | null): value is ThemePreference {
  return value === "dark" || value === "light";
}

function storage(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function sharedCookieDomain(): string {
  if (typeof window === "undefined") return "";
  const host = window.location.hostname.toLowerCase();
  if (host === "tktic.com" || host.endsWith(".tktic.com")) {
    return "Domain=.tktic.com";
  }
  return "";
}

function secureCookieFlag(): string {
  if (typeof window === "undefined") return "";
  return window.location.protocol === "https:" ? "Secure" : "";
}

function readThemeCookie(): ThemePreference | null {
  if (typeof document === "undefined") return null;
  const cookie = document.cookie
    .split("; ")
    .find((entry) => entry.startsWith(`${THEME_STORAGE_KEY}=`));
  if (!cookie) return null;
  const value = decodeURIComponent(cookie.slice(THEME_STORAGE_KEY.length + 1));
  return isThemePreference(value) ? value : null;
}

function writeThemeCookie(theme: ThemePreference) {
  if (typeof document === "undefined") return;
  document.cookie = [
    `${THEME_STORAGE_KEY}=${encodeURIComponent(theme)}`,
    "Path=/",
    `Max-Age=${THEME_COOKIE_MAX_AGE_SECONDS}`,
    "SameSite=Lax",
    sharedCookieDomain(),
    secureCookieFlag(),
  ]
    .filter(Boolean)
    .join("; ");
}

function persistTheme(theme: ThemePreference) {
  writeThemeCookie(theme);
  const store = storage();
  if (store) {
    try {
      store.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      // Storage failure should not prevent an in-session theme change.
    }
  }
}

export function getInitialTheme(): ThemePreference {
  const cookieTheme = readThemeCookie();
  const store = storage();
  if (cookieTheme) {
    if (store) {
      try {
        store.setItem(THEME_STORAGE_KEY, cookieTheme);
      } catch {
        // Ignore private-browsing / quota failures; the DOM still gets themed.
      }
    }
    return cookieTheme;
  }

  if (!store) return DEFAULT_THEME;

  const current = store.getItem(THEME_STORAGE_KEY);
  if (isThemePreference(current)) {
    writeThemeCookie(current);
    return current;
  }

  const legacy = store.getItem(LEGACY_THEME_STORAGE_KEY);
  if (isThemePreference(legacy)) {
    persistTheme(legacy);
    return legacy;
  }

  return DEFAULT_THEME;
}

export function applyTheme(theme: ThemePreference): ThemePreference {
  if (typeof document !== "undefined") {
    document.documentElement.dataset.theme = theme;
  }
  return theme;
}

export function setTheme(theme: ThemePreference): ThemePreference {
  persistTheme(theme);
  return applyTheme(theme);
}

export function toggleTheme(
  current: ThemePreference = getInitialTheme(),
): ThemePreference {
  return setTheme(current === "dark" ? "light" : "dark");
}

export function initializeThemePreference(): ThemePreference {
  return applyTheme(getInitialTheme());
}
